import $ from "jquery";
import {
    ApiError,
    BaseResponse,
    BaseResponseObject,
    EmptyResponse,
    FAQS,
    Fines,
    GenericPayload,
    getDefaultRequestOptions,
    GetFine,
    RequestOptions,
    Ticket,
} from "./Types";
import {Popup} from "../Popups/Popup";

export interface ErrorWrappedDeferred<TR, TJ = any, TN = any> extends JQuery.Deferred<TR, TJ, TN> {
    ew_done(doneCallback: JQuery.TypeOrArray<JQuery.Deferred.Callback<TR>>,
                       ...doneCallbacks: Array<JQuery.TypeOrArray<JQuery.Deferred.Callback<TR>>>): this;
}

declare const SMARTPRISE_BACKEND_URL: string;
declare const SMARTPRISE_BACKEND_REFRESH_URL: string;

export class SmartpriseApiClient {
    public static BASE_ENDPOINT: string;
    public static RENEW_ENDPOINT: string;
    public static LOGIN: string;

    private static instance: SmartpriseApiClient;

    public static readonly ErrorMap: Record<number, string> = {
        // "-7": "Mail non valida",
        // 20: "La password deve contenere tra gli 8 e i 32 caratteri, almeno una lettera maiuscola, una minuscola e un carattere tra [#?!@$%^&*-]",
        20: "Credenziali non valide",
        21: "Password troppo lunga",
        22: "Password troppo corta",
        41: "Invio email fallito"
    }

    private constructor() {
        SmartpriseApiClient.BASE_ENDPOINT = SMARTPRISE_BACKEND_URL;
        SmartpriseApiClient.RENEW_ENDPOINT = SMARTPRISE_BACKEND_REFRESH_URL;

        SmartpriseApiClient.LOGIN = window.location.origin + window.location.pathname + "?page=login";
    }

    public static getInstance(): SmartpriseApiClient {
        if (!SmartpriseApiClient.instance)
            SmartpriseApiClient.instance = new SmartpriseApiClient();

        return SmartpriseApiClient.instance;
    }

    private _EndApiCall<ST extends BaseResponseObject | BaseResponseObject[], FT extends ApiError>
    (request_options: RequestOptions,
     _deferred: JQuery.Deferred<ST, undefined | string | BaseResponse<ST, FT>>
    ): ErrorWrappedDeferred<ST, undefined | string | BaseResponse<ST, FT>> {

        type response_t = BaseResponse<ST, FT>;
        const deferred = _deferred as ErrorWrappedDeferred<ST, undefined | string | response_t>;

        if (request_options.default_eh)
            deferred.fail(ctx => {
                let err_code: number = void 0;
                if (typeof ctx !== "string") {
                    if (ctx === void 0)
                        ctx = "Errore sconosciuto"
                    else {
                        err_code = ctx.error.code;
                        ctx = ctx.error.code in SmartpriseApiClient.ErrorMap ? SmartpriseApiClient.ErrorMap[ctx.error.code] : ctx.error.message;
                    }
                }

                Popup.DefaultErrorInject(<string>ctx).popup.SetShowCloseCross();

                if (err_code >= 10 && err_code < 20) {
                    setTimeout(() => {
                        window.location.assign(SmartpriseApiClient.LOGIN);
                    }, 7_500)
                }
            });

        deferred.ew_done = (doneCallback: JQuery.Deferred.Callback<ST>,
                                       ...doneCallbacks: JQuery.Deferred.Callback<ST>[]) => {
            const wp = (f: JQuery.Deferred.Callback<ST>) => {

                return (...args: any[]) => {
                    try {
                        f(...args);
                    } catch (e) {
                        console.error(e);
                        console.log(f)
                    }
                }
            }
            deferred.done(wp(doneCallback), ...doneCallbacks.map(wp));
            return deferred;
        }

        if (request_options.show_loading) {
            setTimeout(() => {
                const control = $(`<div class="loading-wp"><div><img src="${request_options.loading_image}" alt=""></div></div>`);
                control.prependTo(document.body);
                deferred.always(() => control.remove())
            }, 1000);
        }

        return deferred;
    }

    private _ProcessApiCallResponse<ST extends BaseResponseObject | BaseResponseObject[] = EmptyResponse, FT extends ApiError = ApiError>
    (params: GenericPayload,
     method: string,
     resolver: (content: object) => ST,
     request: () => JQuery.jqXHR<BaseResponse<ST, FT>>,
     request_options: RequestOptions,
     wraps: JQuery.Deferred<ST, string | undefined | BaseResponse<ST, FT>> = null,
    ): JQuery.Deferred<ST, string | undefined | BaseResponse<ST, FT>> {
        type d_type = string | BaseResponse<ST, FT>;
        const deferred = wraps ?? $.Deferred<ST, d_type>();

        deferred.fail(e => (e))

        request().then(
            (data: BaseResponse<ST, FT>) => {
                if (!data.success) {
                    deferred.reject(data);
                    return;
                }

                try {
                    deferred.resolve(resolver(data.result));
                } catch (e) {
                    deferred.reject(`Invalid response from ${method}`);
                    console.error(e);
                }
            }, e => {
                const js = e.responseJSON as BaseResponse<ST, FT> | undefined;

                if (e.status == 401 && request_options.fail_handle_session_handle && js !== void 0) {
                    if (js.error.code === 12) { // ACCESS_TOKEN_EXPIRED
                        this._RenewToken()
                            .done(() => this._ProcessApiCallResponse(params, method, resolver, request, request_options, wraps))
                            .fail(() => document.location = SmartpriseApiClient.LOGIN);
                        return;
                    } else if (js.error.code === 13 || js.error.code === 14 || js.error.code === 15) { // REFRESH_TOKEN_EXPIRED|USER_DISABLED|USER_INVALID_CREDENTIALS
                        document.location = SmartpriseApiClient.LOGIN
                        return;
                    }
                }

                deferred.reject(js);
            });

        return deferred;
    }

    private _RenewToken(): JQuery.jqXHR {
        return $.get(SmartpriseApiClient.RENEW_ENDPOINT);
    }

    private _ApiCall<ST extends BaseResponseObject | BaseResponseObject[] = EmptyResponse>(data: GenericPayload,
                                                                                           resolver: (content: object) => ST,
                                                                                           method: string,
                                                                                           request_options: RequestOptions) {
        const url = SmartpriseApiClient.BASE_ENDPOINT + "?method=" + method;
        const payload = {
            url: url,
            type: "POST",
            data: JSON.stringify(data),
            contentType: "application/json; charset=utf-8",
            dataType: "json"
        };

        const request = () => $.ajax(payload);

        request_options ??= getDefaultRequestOptions();
        const deferred = this._ProcessApiCallResponse(data, method, resolver, request, request_options);

        return this._EndApiCall(request_options, deferred)
    }

    private ApiCall<ST extends BaseResponseObject = EmptyResponse>(type: { new(v: object): ST },
                                                                   params: GenericPayload,
                                                                   method: string,
                                                                   request_options: RequestOptions) {
        return this._ApiCall(params,
            (content: object) => new type(content),
            method,
            request_options);
    }

    private ArrayApiCall<ST extends BaseResponseObject = EmptyResponse>(type: { new(v: object): ST },
                                                                        params: GenericPayload,
                                                                        method: string,
                                                                        request_options: RequestOptions) {
        return this._ApiCall(params,
            (content: object[]): ST[] => content.map(x => new type(x)),
            method,
            request_options);
    }

    /** AUTH */

    public Login(user: string,
                 password: string,
                 lang: string,
                 request_options?: RequestOptions) {
        return this.ApiCall(EmptyResponse,
            {
                user: user,
                password: password,
                lang: lang,
            },
            "rest\\customers\\login", request_options)
    }

    public log_out(request_options?: RequestOptions) {
        return this.ApiCall(EmptyResponse, {}, "rest\\customers\\logout", request_options);
    }
    /** END AUTH */

    public get_faqs(request_options?: RequestOptions) {
        return this.ArrayApiCall(FAQS, {}, "rest\\customers\\get_faqs", request_options);
    }

    public get_fines(request_options?: RequestOptions) {
        return this.ArrayApiCall(Fines, {}, "rest\\customers\\get_fines", request_options);
    }

    public get_tickets(email: string, request_options?: RequestOptions) {
        return this.ArrayApiCall(Ticket, {email: email}, "rest\\customers\\get_tickets", request_options);
    }

    public create_ticket(
        name: string,
        email: string,
        subject: string,
        message: string,
        attachment1: {
            b64: string
            file_name: string
            mime_type: string
        } | null,
        attachment2: {
            b64: string
            file_name: string
            mime_type: string
        } | null,
        verbal_id: string,
        request_options?: RequestOptions) {
        return this.ApiCall(Ticket, {
            name: name,
            email: email,
            subject: subject,
            message: message,
            attachment1: attachment1,
            attachment2: attachment2,
            fine_id: verbal_id,
        }, "rest\\customers\\create_ticket", request_options);
    }

    public get_fine(smartprise_id: number, request_options?: RequestOptions) {
        return this.ApiCall(GetFine, {smartprise_id: smartprise_id},
            "rest\\customers\\get_fine", request_options)
    }

    public change_langauge(
        lang: string,
        request_options?: RequestOptions) {
        return this.ApiCall(EmptyResponse, {lang: lang}, "rest\\customers\\change_lang", request_options)
    }
}
